import { Inject, Injectable } from '@angular/core'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { MatDialog, MatDialogConfig } from '@angular/material/dialog'

import { SiteService } from '../services/site.service'
import { MessageService } from './message.service'
import { TranslateService } from '@ngx-translate/core'
import { ICustomerAccountValidationResult } from '../types/customerAccountValidationResult'
import { ExistingChangeRequestViewModel } from '../models/existingChangeRequestViewModel'
import { ErrorMessageModalComponent } from '../components/modals/error-message-modal/error-message-modal.component'
import { RequestProceedModalComponent } from '../components/modals/request-proceed-modal/request-proceed-modal.component'
import { RequestConfirmationModalComponent } from '../components/modals/request-confirmation-modal/request-confirmation-modal.component'
import { RequestSummaryModalComponent } from '../components/modals/request-summary-modal/request-summary-modal.component'

@Injectable({
  providedIn: 'root'
})
export class CustomerRequestsService 
{

	constructor(
			private http: HttpClient,
			private siteService: SiteService,
			private messageService: MessageService,
			private translateService: TranslateService,
			private dialog: MatDialog,
			@Inject('BASE_API_URL') private baseUrl: string,
	) 
	{ 
	}

	getNonSessionHeaders()
	{
		return new HttpHeaders({
			'Content-Type': 'application/json',
			'Accept': 'application/json'
		})
	}

	getNonContentTypeHeaders()
	{
		return new HttpHeaders({
			'Accept': 'application/json',
			'CustomerAuthToken': this.siteService.customerAccount?.customerValidationKey ?? ''
		})
	}

	getHttpHeaders()
	{
		return new HttpHeaders({
			'Content-Type': 'application/json',
			'Accept': 'application/json',
			'CustomerAuthToken': this.siteService.customerAccount?.customerValidationKey ?? ''
		})
	}

	addCustomerIntent(customer: any)
	{
		return this
			.http
			.post<any>(this.baseUrl + 'ICustomers/AddCustomerIntent', JSON.stringify(customer), { headers: this.getNonSessionHeaders() })
	}

	validateCustomerAccount(customerInfo: any)
	{
		return this
			.http
			.post<any>(this.baseUrl + 'ICustomers/ValidateCustomerAccount', JSON.stringify(customerInfo), { headers: this.getNonSessionHeaders() })
	}

	getChangeRequestState(request: any)
	{
		return this
			.http
			.post<any>(this.baseUrl + 'ICustomers/GetChangeRequestState', JSON.stringify(request), { headers: this.getNonSessionHeaders() })
	}

	extendSession(changeRequest: any)
	{
		return this
			.http
			.post<any>(this.baseUrl + 'ICustomers/ExtendSession', JSON.stringify(changeRequest), { headers: this.getHttpHeaders() })
	}

	getExistingChangeRequests(request: any)
	{
		return this
			.http
			.post<any>(this.baseUrl + 'ICustomers/GetExistingChangeRequests', JSON.stringify(request), { headers: this.getHttpHeaders() })
	}

	getPaymentDateChangeViewModel(pageIndex: number, pageSize: number)
	{
		return this
			.http
			.get<any>(this.baseUrl + 'ICustomers/GetPaymentDateChangeViewModel/' + pageIndex + '/' + pageSize, { headers: this.getHttpHeaders() })
	}

	addPaymentDateChangeRequest(changeRequest: any)
	{
		return this
			.http
			.post<any>(this.baseUrl + 'ICustomers/AddPaymentDateChangeRequest', JSON.stringify(changeRequest), { headers: this.getHttpHeaders() })
	}

	getPaymentDateChangeSummary(referenceNumber: string)
	{
		return this
			.http
			.get<any>(this.baseUrl + 'ICustomers/GetPaymentDateChangeSummary/' + referenceNumber, { headers: this.getHttpHeaders() })
	}

	getBankAccountChangeViewModel()
	{
		return this
			.http
			.get<any>(this.baseUrl + 'ICustomers/GetBankAccountChangeViewModel', { headers: this.getHttpHeaders() })
	}

	getBankAccountChangeSummary(referenceNumber: string)
	{
		return this
			.http
			.get<any>(this.baseUrl + 'ICustomers/GetBankAccountChangeSummary/' + referenceNumber, { headers: this.getHttpHeaders() })
	}

	addBankAccountChangeRequest(formData: FormData)
	{
		return this
			.http
			.post<any>(this.baseUrl + 'ICustomers/AddBankAccountChangeRequest', formData, { headers: this.getNonContentTypeHeaders() })
	}

	getNameChangeViewModel()
	{
		return this
			.http
			.get<any>(this.baseUrl + 'ICustomers/GetNameChangeViewModel', { headers: this.getHttpHeaders() })
	}

	addNameChangeRequest(formData: FormData )
	{
		return this
			.http
			.post<any>(this.baseUrl + 'ICustomers/AddNameChangeRequest', formData, { headers: this.getNonContentTypeHeaders() })
	}

	getNameChangeSummary(referenceNumber: string)
	{
		return this
			.http
			.get<any>(this.baseUrl + 'ICustomers/GetNameChangeSummary/' + referenceNumber, { headers: this.getHttpHeaders() })
	}

	getAddressChangeRequestViewModel()
	{
		return this
			.http
			.get<any>(this.baseUrl + 'ICustomers/GetAddressChangeViewModel', { headers: this.getHttpHeaders() })
	}

	addAddressChangeRequest(formData: FormData)
	{
		return this
			.http
			.post<any>(this.baseUrl + 'ICustomers/AddAddressChangeRequest', formData, { headers: this.getNonContentTypeHeaders() })
	}

	getEmailChangeRequestViewModel()
	{
		return this
			.http
			.get<any>(this.baseUrl + 'ICustomers/GetEmailChangeViewModel', { headers: this.getHttpHeaders() })
	}

	validateCustomerEmail(changeRequest: any)
	{
		return this
			.http
			.post<any>(this.baseUrl + 'ICustomers/ValidateCustomerEmail', JSON.stringify(changeRequest), { headers: this.getHttpHeaders() })
	}

	addEmailChangeRequest(changeRequest: any)
	{
		return this
			.http
			.post<any>(this.baseUrl + 'ICustomers/AddEmailChangeRequest', JSON.stringify(changeRequest), { headers: this.getHttpHeaders() })
	}

	getLeaseTransferRequestViewModel()
	{
		return this
			.http
			.get<any>(this.baseUrl + 'ICustomers/GetLeaseTransferViewModel', { headers: this.getHttpHeaders() })
	}

	addLeaseTransferRequest(changeRequest: any)
	{
		return this
			.http
			.post<any>(this.baseUrl + 'ICustomers/AddLeaseTransferRequest', JSON.stringify(changeRequest), { headers: this.getHttpHeaders() })
	}

	getOtherChangeRequestViewModel()
	{
		return this
			.http
			.get<any>(this.baseUrl + 'ICustomers/GetOtherChangeRequestViewModel', { headers: this.getHttpHeaders() })
	}

	addOtherChangeRequest(changeRequest: any)
	{
		return this
			.http
			.post<any>(this.baseUrl + 'ICustomers/AddOtherChangeRequest', JSON.stringify(changeRequest), { headers: this.getHttpHeaders() })
	}
	
	getPhoneChangeRequestViewModel()
	{
		return this
			.http
			.get<any>(this.baseUrl + 'ICustomers/GetPhoneChangeViewModel', { headers: this.getHttpHeaders() })
	}

	addPhoneChangeRequest(changeRequest: any)
	{
		return this
			.http
			.post<any>(this.baseUrl + 'ICustomers/AddPhoneChangeRequest', JSON.stringify(changeRequest), { headers: this.getHttpHeaders() })
	}

	checkChangeRequestsAndProceed(customerRequestTypeId: number)
	{
		return new Promise((resolve, reject) =>
		{
			let request =
			{
				customerChangeRequestTypeId: customerRequestTypeId
			}

			this.messageService.startedHttpRequest('checkChangeRequestsAndProceed')
			this.getExistingChangeRequests(request)
			.subscribe({
				next: (apiResponse) =>
				{
					let existingChangeRequestViewModel = new ExistingChangeRequestViewModel()
					existingChangeRequestViewModel = apiResponse

					if(existingChangeRequestViewModel.hasExistingChangeRequest)
					{
						const dialogConfig = new MatDialogConfig()
						dialogConfig.width = '500px',
						dialogConfig.data = {
							customerChangeRequestTypeId: request.customerChangeRequestTypeId,
						}		

						const dialogRef = this.dialog.open(RequestProceedModalComponent, dialogConfig)
						dialogRef.afterClosed().subscribe(result => {
							resolve(result)
						})
					}
					else
					{
						resolve(true)
					}
				},
				error: (error) =>
				{
					this.messageService.completedHttpRequest('checkChangeRequestsAndProceed')
				
					const dialogConfig = new MatDialogConfig()
					dialogConfig.width = '500px',
					dialogConfig.data = {
						title: this.translateService.instant('COMMON_LBL_ERROR'),
						message: this.translateService.instant('COMMON_LBL_ERROR_MSG')
					}		
					this.dialog.open(ErrorMessageModalComponent, dialogConfig)		
					resolve(false)
				},
				complete: () =>
				{
					this.messageService.completedHttpRequest('checkChangeRequestsAndProceed')
				}
			})
		})
	}

	requestConfirmation(heading: string, requestSummaryList: any)
	{
		return new Promise((resolve, reject) =>
		{
			const dialogConfig = new MatDialogConfig()
			dialogConfig.width = '500px',
			dialogConfig.data = {
				heading: heading,
				requestSummaryList: requestSummaryList
			}
		
			const dialogRef = this.dialog.open(RequestConfirmationModalComponent, dialogConfig)	
			dialogRef.afterClosed().subscribe(result => {
				resolve(result)
			})
		})
	}

	requestSummary(referenceNumber: string, heading: string, requestSummaryList: any)
  	{
		const dialogConfig = new MatDialogConfig()
		dialogConfig.width = '600px',
		dialogConfig.data = {
			referenceNumber: referenceNumber,
			heading: heading,
			requestSummaryList: requestSummaryList
		}

		const dialogRef = this.dialog.open(RequestSummaryModalComponent, dialogConfig)
		dialogRef.afterClosed().subscribe(result => {
			if(result)
			{
				this.extendLoginSession()
			}
			else
			{
				this.siteService.logout()
			}
		})
  	}

	extendLoginSession()
	{
		let request =
		{
		}

		this.messageService.startedHttpRequest('extendLoginSession')
		this.extendSession(request)
		.subscribe({
			next: (apiResponse) =>
			{
				var customerAccountValidationResult: ICustomerAccountValidationResult = apiResponse
				this.siteService.updateLoginSession(customerAccountValidationResult)
			},
			error: (error) =>
			{
				this.messageService.completedHttpRequest('extendLoginSession')

				const dialogConfig = new MatDialogConfig()
				dialogConfig.width = '500px',
				dialogConfig.data = {
				title: this.translateService.instant('COMMON_LBL_ERROR'),
				message: this.translateService.instant('COMMON_LBL_ERROR_MSG')
				}		
				this.dialog.open(ErrorMessageModalComponent, dialogConfig)
			},
			complete: () =>
			{
				this.messageService.completedHttpRequest('extendLoginSession')
			}
		})
	}
}
